import * as Sentry from "@sentry/nextjs";
import type { AvailabilityProps } from "forms/fields/Availability/Availability";
import type { ContactDataProps, ShortContactDataProps } from "forms/fields/ContactData/types";
import type { ExpectedSalaryProps } from "forms/fields/ExpectedSalary/ExpectedSalary";
import type { LocationMultiProps } from "forms/fields/LocationMulti/LocationMulti";
import type { LocationSingleProps } from "forms/fields/LocationSingle/LocationSingle";
import dynamic from "next/dynamic";
import type { FormControlType } from "types/formType";
import type { IJsonSchema, IProperty } from "types/jsonSchema";
import { questionNames } from "types/QuestionNames";
import type { IFormInputs } from "types/uiSchema";
import { logger } from "utils/helpers/logger";

const Availability = dynamic(() => import("forms/fields/Availability/Availability"));
const ContactData = dynamic(() => import("forms/fields/ContactData/ContactData"));
const ShortContactData = dynamic(() => import("forms/fields/ContactData/ShortContactData"));
const DriverLicense = dynamic(() => import("forms/fields/DriverLicense/DriverLicense"));
const ExpectedSalary = dynamic(() => import("forms/fields/ExpectedSalary/ExpectedSalary"));
const AddFile = dynamic(() => import("forms/fields/File/File"));
const Languages = dynamic(() => import("forms/fields/Languages/Languages"));
const MultiCheckbox = dynamic(() => import("forms/fields/MultiCheckbox/MultiCheckbox"));
const MultiSelect = dynamic(() => import("forms/fields/MultiSelect/MultiSelect"));
const Radio = dynamic(() => import("forms/fields/Radio/Radio"));
const Select = dynamic(() => import("forms/fields/Select/Select"));
const TextBox = dynamic(() => import("forms/fields/TextBox/TextBox"));
const DatePicker = dynamic(() => import("forms/fields/DatePicker/DatePicker"));
const PersonalData = dynamic(() => import("forms/fields/PersonalData/PersonalData"));
const RecommendingPerson = dynamic(() => import("forms/fields/RecommendingPerson/RecommendingPerson"));
const CvField = dynamic(() => import("forms/fields/Cv/Cv"));
const Clauses = dynamic(() => import("forms/fields/Clauses/Clauses"));
const Consents = dynamic(() => import("forms/fields/Consents/Consents"));
const Section = dynamic(() => import("forms/fields/Section/Section"));
const LocationSingle = dynamic(() => import("forms/fields/LocationSingle/LocationSingle"));
const LocationMulti = dynamic(() => import("forms/fields/LocationMulti/LocationMulti"));

interface GenerateFormProps {
    field: IFormInputs;
    control: FormControlType;
    jsonData: Record<string, IProperty>;
    allOf?: IJsonSchema["allOf"];
    defaultValue?: unknown;
}

export const renderField = ({ field, control, jsonData, defaultValue, allOf = [] }: GenerateFormProps) => {
    try {
        const commonProps = { control, ...jsonData };

        switch (field["x-control-name"]) {
            case questionNames.Section:
                return <Section uiData={field} />;
            case questionNames.Availability:
                return <Availability {...(commonProps as AvailabilityProps)} />;
            case questionNames.AddFile:
                return <AddFile control={control} uiData={field} jsonData={jsonData} />;
            case questionNames.ExpectedSalary:
            case questionNames.ExpectedSalaryUsd:
            case questionNames.ExpectedSalaryEur:
            case questionNames.ExpectedSalaryUah:
                return (
                    <ExpectedSalary
                        {...(commonProps as ExpectedSalaryProps)}
                        dictionaryReferenceProperty={field?.elements?.[0]?.["x-dictionary-reference-property"] ?? ""}
                    />
                );
            case questionNames.ShortContactData:
                return <ShortContactData {...(commonProps as ShortContactDataProps)} allOf={allOf} />;
            case questionNames.ContactData:
                return <ContactData {...(commonProps as ContactDataProps)} allOf={allOf} />;
            case questionNames.TextBox:
                return <TextBox uiData={field} control={control} />;
            case questionNames.MultiCheckbox:
                return <MultiCheckbox control={control} uiData={field} />;
            case questionNames.Dropdown:
                return <Select control={control} uiData={field} />;
            case questionNames.RadioButton:
                return <Radio control={control} uiData={field} />;
            case questionNames.DriverLicense:
                return <DriverLicense control={control} uiData={field} />;
            case questionNames.Multiselect:
                return <MultiSelect control={control} uiData={field} />;
            case questionNames.Languages:
                return <Languages control={control} jsonData={jsonData} />;
            case questionNames.DatePicker:
                return <DatePicker control={control} uiData={field} jsonData={jsonData} />;
            case questionNames.PersonalData2:
                return <PersonalData control={control} uiData={field} />;
            case questionNames.RecommendingPerson:
                return <RecommendingPerson control={control} uiData={field} />;
            case questionNames.CvFile:
                return <CvField control={control} uiData={field} jsonData={jsonData} defaultCv={defaultValue as File | null} />;
            case questionNames.Clauses:
                return <Clauses uiData={field} />;
            case questionNames.Consents:
                return <Consents control={control} uiData={field} />;
            case questionNames.LocationSingle:
                return <LocationSingle {...(commonProps as LocationSingleProps)} uiData={field} />;
            case questionNames.LocationMulti:
                return <LocationMulti {...(commonProps as LocationMultiProps)} uiData={field} />;
            case "Greeting":
                return null;
            default: {
                logger.log("useRenderFields -> Field not rendered", field["x-control-name"], field.description);
                return null;
            }
        }
    } catch (e) {
        Sentry.captureException(e, { extra: { field } });
        return null;
    }
};
